// masterTemplateScenarios.js
import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { RocketLaunch, CheckCircle } from '@mui/icons-material';

// ==================== MASTER TEMPLATE DATABASE ====================
export const masterTemplateScenarios = [
  {
    _id: "template_auto_oem",
    name: "Automotive OEM - Traditional Manufacturer",
    description: "Large-scale automotive manufacturer with complex supply chains and regulatory requirements",
    industry: "automotive",
    tier: "OEM",
    persona: "CFO",
    turnover: 500000000,
    projectionYear: 3,
    totalImpact: 75000000,
    createdAt: "2024-01-01T00:00:00Z",
    isTemplate: true,
    templateCategory: "automotive",
    
    values: {
      sustainabilityPressure: { current: 4, target: 6 },
      esgCompliance: { current: 4, target: 7 },
      greenProductInnovation: { current: 3, target: 5 },
      supplyChainCircularity: { current: 3, target: 5 },
      supplierDependency: { current: 4, target: 6 },
      collaborationNeed: { current: 4, target: 7 },
      geographicSpread: { current: 3, target: 6 },
      supplierToolsGap: { current: 4, target: 6 },
      supplierSLAPressure: { current: 5, target: 7 },
      partnerEcosystemMaturity: { current: 5, target: 7 },
      serviceRevenue: { current: 3, target: 5 },
      businessModelShift: { current: 4, target: 6 },
      lifecycleMonetization: { current: 3, target: 5 },
      digitalThreadMonetization: { current: 4, target: 6 },
      productComplexity: { current: 6, target: 8 },
      changeFrequency: { current: 4, target: 6 },
      volumeVarietyMix: { current: 5, target: 7 },
      cadIntensity: { current: 5, target: 7 },
      lifecycleLength: { current: 5, target: 7 },
      reusePotential: { current: 4, target: 6 },
      partCount: { current: 6, target: 8 },
      productConfigurability: { current: 5, target: 7 },
      portfolioBreadth: { current: 6, target: 8 },
      programDuration: { current: 5, target: 7 },
      crossDomainNeed: { current: 4, target: 6 },
      legacyProcessGap: { current: 5, target: 7 },
      digitalMaturity: { current: 4, target: 6 },
      infraReadiness: { current: 4, target: 6 },
      toolFragmentation: { current: 5, target: 7 },
      manualExceptions: { current: 4, target: 6 },
      systemAutomation: { current: 3, target: 5 },
      exceptionVolume: { current: 4, target: 6 },
      testInfrastructure: { current: 4, target: 6 },
      skillDiversity: { current: 4, target: 6 },
      userBuyIn: { current: 3, target: 5 },
      changeResistance: { current: 4, target: 6 },
      plmTalentAvailability: { current: 3, target: 5 },
      integrationDepth: { current: 4, target: 6 },
      cloudOpsDiscipline: { current: 3, target: 5 },
      eventDrivenIntegration: { current: 2, target: 4 },
      digitalTwinConvergence: { current: 3, target: 5 },
      dataLatencyTolerance: { current: 4, target: 6 },
      crossPlmInteroperability: { current: 3, target: 5 },
      platformEcosystemParticipation: { current: 4, target: 6 },
      speedSensitivity: { current: 5, target: 7 },
      competitivePressure: { current: 5, target: 7 },
      innovationThroughput: { current: 4, target: 6 },
      customerFeedbackLoop: { current: 3, target: 5 },
      timeToValueAcceleration: { current: 4, target: 6 },
      customerCoCreationCapability: { current: 3, target: 5 },
      regulatoryBurden: { current: 6, target: 8 },
      recallRisk: { current: 5, target: 7 },
      traceabilityNeed: { current: 5, target: 8 },
      compliancePenalty: { current: 4, target: 6 },
      namingDiscipline: { current: 4, target: 6 },
      dataGovernanceQuality: { current: 4, target: 7 },
      endToEndTraceability: { current: 4, target: 7 },
      dataProvenance: { current: 3, target: 5 },
      regulatoryAuditReadiness: { current: 4, target: 7 },
      errorCost: { current: 5, target: 7 },
      delayPenalty: { current: 5, target: 7 },
      engineeringCost: { current: 5, target: 7 },
      reuseLibrary: { current: 4, target: 6 },
      ipRisk: { current: 5, target: 7 },
      aftermarketFocus: { current: 3, target: 5 },
      aiReadiness: { current: 3, target: 5 },
      analyticsCapability: { current: 4, target: 6 },
      aiCoDesignAdoption: { current: 2, target: 4 },
      intelligentWorkflowOrchestration: { current: 3, target: 5 }
    },
    
    parameters: {
      annualGrowthRate: 4.5,
      discountRate: 8,
      implementationCostFactor: 0.15,
      riskAdjustmentFactor: 0.85,
      marginImprovementTarget: 0.12,
      efficiencyGainTarget: 0.18,
      qualityImprovementTarget: 0.25,
      timeToMarketTarget: 0.22
    }
  },

  {
    _id: "template_aero_defense",
    name: "Aerospace & Defense - High Compliance",
    description: "Highly regulated environment with long product lifecycles and stringent quality requirements",
    industry: "aerospace",
    tier: "Tier 1",
    persona: "Quality Director",
    turnover: 250000000,
    projectionYear: 5,
    totalImpact: 45000000,
    createdAt: "2024-01-02T00:00:00Z",
    isTemplate: true,
    templateCategory: "aerospace",
    
    values: {
      sustainabilityPressure: { current: 5, target: 7 },
      esgCompliance: { current: 6, target: 9 },
      productComplexity: { current: 8, target: 9 },
      changeFrequency: { current: 4, target: 5 },
      lifecycleLength: { current: 8, target: 9 },
      regulatoryBurden: { current: 8, target: 10 },
      traceabilityNeed: { current: 8, target: 10 },
      endToEndTraceability: { current: 7, target: 10 },
      dataGovernanceQuality: { current: 7, target: 9 },
      errorCost: { current: 8, target: 10 },
      compliancePenalty: { current: 7, target: 9 },
      digitalMaturity: { current: 5, target: 7 },
      integrationDepth: { current: 5, target: 7 },
      supplierDependency: { current: 6, target: 8 },
      crossDomainNeed: { current: 6, target: 8 },
      digitalTwinConvergence: { current: 4, target: 6 },
      aiReadiness: { current: 3, target: 5 }
    },
    
    parameters: {
      annualGrowthRate: 3.5,
      discountRate: 10,
      implementationCostFactor: 0.20,
      riskAdjustmentFactor: 0.75,
      marginImprovementTarget: 0.08,
      efficiencyGainTarget: 0.15,
      qualityImprovementTarget: 0.35,
      timeToMarketTarget: 0.15
    }
  },

  {
    _id: "template_consumer_electronics",
    name: "Consumer Electronics - Fast Innovation",
    description: "High-velocity product development with short lifecycles and frequent innovation",
    industry: "electronics",
    tier: "Brand Owner",
    persona: "Product Manager",
    turnover: 150000000,
    projectionYear: 2,
    totalImpact: 30000000,
    createdAt: "2024-01-03T00:00:00Z",
    isTemplate: true,
    templateCategory: "electronics",
    
    values: {
      speedSensitivity: { current: 7, target: 9 },
      competitivePressure: { current: 7, target: 9 },
      innovationThroughput: { current: 6, target: 8 },
      changeFrequency: { current: 7, target: 9 },
      lifecycleLength: { current: 3, target: 4 },
      timeToValueAcceleration: { current: 5, target: 7 },
      supplierDependency: { current: 6, target: 8 },
      collaborationNeed: { current: 5, target: 7 },
      digitalMaturity: { current: 5, target: 7 },
      regulatoryBurden: { current: 4, target: 6 },
      sustainabilityPressure: { current: 5, target: 7 },
      aiReadiness: { current: 4, target: 6 },
      customerCoCreationCapability: { current: 5, target: 7 }
    },
    
    parameters: {
      annualGrowthRate: 8.5,
      discountRate: 12,
      implementationCostFactor: 0.10,
      riskAdjustmentFactor: 0.90,
      marginImprovementTarget: 0.15,
      efficiencyGainTarget: 0.25,
      qualityImprovementTarget: 0.18,
      timeToMarketTarget: 0.35
    }
  },

  {
    _id: "template_medical_devices",
    name: "Medical Devices - Life Critical",
    description: "Highly regulated medical device manufacturer with focus on patient safety and quality",
    industry: "medical",
    tier: "Regulated Manufacturer",
    persona: "Regulatory Affairs",
    turnover: 80000000,
    projectionYear: 4,
    totalImpact: 25000000,
    createdAt: "2024-01-04T00:00:00Z",
    isTemplate: true,
    templateCategory: "medical",
    
    values: {
      regulatoryBurden: { current: 9, target: 10 },
      traceabilityNeed: { current: 9, target: 10 },
      recallRisk: { current: 8, target: 10 },
      errorCost: { current: 9, target: 10 },
      compliancePenalty: { current: 8, target: 10 },
      dataGovernanceQuality: { current: 8, target: 10 },
      endToEndTraceability: { current: 8, target: 10 },
      regulatoryAuditReadiness: { current: 8, target: 10 },
      dataProvenance: { current: 7, target: 9 },
      productComplexity: { current: 6, target: 7 },
      changeFrequency: { current: 3, target: 4 },
      lifecycleLength: { current: 7, target: 8 },
      businessModelShift: { current: 3, target: 4 },
      serviceRevenue: { current: 4, target: 5 }
    },
    
    parameters: {
      annualGrowthRate: 6.5,
      discountRate: 9,
      implementationCostFactor: 0.25,
      riskAdjustmentFactor: 0.70,
      marginImprovementTarget: 0.10,
      efficiencyGainTarget: 0.12,
      qualityImprovementTarget: 0.40,
      timeToMarketTarget: 0.10
    }
  },

  {
    _id: "template_industrial_equipment",
    name: "Industrial Equipment - Service Focused",
    description: "Heavy equipment manufacturer transitioning to service-based business model",
    industry: "industrial",
    tier: "Tier 1 Supplier",
    persona: "Service Director",
    turnover: 120000000,
    projectionYear: 5,
    totalImpact: 35000000,
    createdAt: "2024-01-05T00:00:00Z",
    isTemplate: true,
    templateCategory: "industrial",
    
    values: {
      serviceRevenue: { current: 5, target: 8 },
      aftermarketFocus: { current: 5, target: 8 },
      lifecycleMonetization: { current: 4, target: 7 },
      digitalThreadMonetization: { current: 3, target: 6 },
      businessModelShift: { current: 4, target: 7 },
      lifecycleLength: { current: 7, target: 9 },
      productComplexity: { current: 6, target: 7 },
      changeFrequency: { current: 3, target: 4 },
      digitalTwinConvergence: { current: 4, target: 7 },
      sensorizationLevel: { current: 4, target: 7 },
      customerFeedbackLoop: { current: 4, target: 6 },
      regulatoryBurden: { current: 5, target: 7 },
      sustainabilityPressure: { current: 4, target: 6 }
    },
    
    parameters: {
      annualGrowthRate: 4.0,
      discountRate: 7,
      implementationCostFactor: 0.18,
      riskAdjustmentFactor: 0.80,
      marginImprovementTarget: 0.14,
      efficiencyGainTarget: 0.16,
      qualityImprovementTarget: 0.22,
      timeToMarketTarget: 0.18
    }
  },

  {
    _id: "template_software_platform",
    name: "Software Platform - Digital Native",
    description: "Software-intensive company with platform business model and rapid iteration",
    industry: "software",
    tier: "Digital Native",
    persona: "CTO",
    turnover: 75000000,
    projectionYear: 3,
    totalImpact: 40000000,
    createdAt: "2024-01-06T00:00:00Z",
    isTemplate: true,
    templateCategory: "software",
    
    values: {
      digitalMaturity: { current: 7, target: 9 },
      cloudOpsDiscipline: { current: 6, target: 9 },
      aiReadiness: { current: 5, target: 8 },
      analyticsCapability: { current: 6, target: 8 },
      aiCoDesignAdoption: { current: 4, target: 7 },
      intelligentWorkflowOrchestration: { current: 5, target: 8 },
      platformEcosystemParticipation: { current: 5, target: 8 },
      speedSensitivity: { current: 7, target: 9 },
      innovationThroughput: { current: 6, target: 9 },
      changeFrequency: { current: 8, target: 9 },
      timeToValueAcceleration: { current: 6, target: 8 },
      businessModelShift: { current: 6, target: 9 },
      serviceRevenue: { current: 7, target: 9 },
      lifecycleMonetization: { current: 5, target: 8 },
      productComplexity: { current: 4, target: 6 },
      partCount: { current: 2, target: 3 },
      cadIntensity: { current: 2, target: 3 }
    },
    
    parameters: {
      annualGrowthRate: 12.5,
      discountRate: 15,
      implementationCostFactor: 0.08,
      riskAdjustmentFactor: 0.95,
      marginImprovementTarget: 0.25,
      efficiencyGainTarget: 0.30,
      qualityImprovementTarget: 0.20,
      timeToMarketTarget: 0.40
    }
  },

  {
    _id: "template_startup_growth",
    name: "Tech Startup - Growth Phase",
    description: "Fast-growing startup scaling operations and formalizing processes",
    industry: "startup",
    tier: "Growth Stage",
    persona: "Founder",
    turnover: 15000000,
    projectionYear: 2,
    totalImpact: 8000000,
    createdAt: "2024-01-07T00:00:00Z",
    isTemplate: true,
    templateCategory: "startup",
    
    values: {
      digitalMaturity: { current: 2, target: 5 },
      systemAutomation: { current: 1, target: 4 },
      toolFragmentation: { current: 8, target: 5 },
      manualExceptions: { current: 7, target: 4 },
      infraReadiness: { current: 3, target: 6 },
      speedSensitivity: { current: 8, target: 9 },
      timeToValueAcceleration: { current: 7, target: 9 },
      innovationThroughput: { current: 6, target: 8 },
      regulatoryBurden: { current: 2, target: 3 },
      traceabilityNeed: { current: 2, target: 4 },
      userBuyIn: { current: 5, target: 7 },
      changeResistance: { current: 4, target: 6 },
      plmTalentAvailability: { current: 2, target: 4 }
    },
    
    parameters: {
      annualGrowthRate: 25.0,
      discountRate: 20,
      implementationCostFactor: 0.05,
      riskAdjustmentFactor: 1.0,
      marginImprovementTarget: 0.30,
      efficiencyGainTarget: 0.35,
      qualityImprovementTarget: 0.15,
      timeToMarketTarget: 0.45
    }
  },

  {
    _id: "template_enterprise_digital_transformation",
    name: "Enterprise Digital Transformation",
    description: "Large established company undergoing comprehensive digital transformation",
    industry: "multinational",
    tier: "Enterprise",
    persona: "CIO",
    turnover: 1000000000,
    projectionYear: 5,
    totalImpact: 150000000,
    createdAt: "2024-01-08T00:00:00Z",
    isTemplate: true,
    templateCategory: "transformation",
    
    values: {
      legacyProcessGap: { current: 7, target: 4 },
      technicalDebt: { current: 8, target: 3 },
      toolFragmentation: { current: 8, target: 4 },
      manualExceptions: { current: 7, target: 3 },
      legacyCustomizationDebt: { current: 7, target: 3 },
      digitalMaturity: { current: 3, target: 7 },
      digitalThreadContinuity: { current: 2, target: 7 },
      digitalTwinConvergence: { current: 2, target: 6 },
      cloudOpsDiscipline: { current: 2, target: 7 },
      integrationDepth: { current: 3, target: 8 },
      userBuyIn: { current: 3, target: 7 },
      changeResistance: { current: 7, target: 3 },
      skillDiversity: { current: 3, target: 6 },
      plmTalentAvailability: { current: 2, target: 5 },
      aiReadiness: { current: 2, target: 6 },
      analyticsCapability: { current: 3, target: 7 },
      intelligentWorkflowOrchestration: { current: 2, target: 6 }
    },
    
    parameters: {
      annualGrowthRate: 2.5,
      discountRate: 6,
      implementationCostFactor: 0.30,
      riskAdjustmentFactor: 0.65,
      marginImprovementTarget: 0.08,
      efficiencyGainTarget: 0.20,
      qualityImprovementTarget: 0.25,
      timeToMarketTarget: 0.20
    }
  },

  {
    _id: "template_quick_compliance",
    name: "Quick Start: Compliance Focus",
    description: "Focus on regulatory compliance and quality management",
    industry: "regulated",
    tier: "Compliance Critical",
    persona: "Quality Manager",
    turnover: 50000000,
    projectionYear: 3,
    totalImpact: 12000000,
    createdAt: "2024-01-09T00:00:00Z",
    isTemplate: true,
    templateCategory: "quickstart",
    
    values: {
      regulatoryBurden: { current: 5, target: 8 },
      traceabilityNeed: { current: 4, target: 7 },
      dataGovernanceQuality: { current: 4, target: 7 },
      endToEndTraceability: { current: 3, target: 6 },
      errorCost: { current: 5, target: 8 },
      compliancePenalty: { current: 4, target: 7 }
    },
    
    parameters: {
      annualGrowthRate: 4.0,
      discountRate: 8,
      implementationCostFactor: 0.12,
      riskAdjustmentFactor: 0.80,
      marginImprovementTarget: 0.10,
      efficiencyGainTarget: 0.15,
      qualityImprovementTarget: 0.30,
      timeToMarketTarget: 0.15
    }
  },

  {
    _id: "template_quick_innovation",
    name: "Quick Start: Innovation Focus",
    description: "Focus on speed to market and product innovation",
    industry: "innovation",
    tier: "Innovation Leader",
    persona: "Product Manager",
    turnover: 30000000,
    projectionYear: 2,
    totalImpact: 15000000,
    createdAt: "2024-01-10T00:00:00Z",
    isTemplate: true,
    templateCategory: "quickstart",
    
    values: {
      speedSensitivity: { current: 6, target: 9 },
      competitivePressure: { current: 6, target: 8 },
      innovationThroughput: { current: 5, target: 8 },
      timeToValueAcceleration: { current: 5, target: 8 },
      changeFrequency: { current: 6, target: 8 },
      customerCoCreationCapability: { current: 4, target: 7 }
    },
    
    parameters: {
      annualGrowthRate: 10.0,
      discountRate: 12,
      implementationCostFactor: 0.10,
      riskAdjustmentFactor: 0.90,
      marginImprovementTarget: 0.20,
      efficiencyGainTarget: 0.25,
      qualityImprovementTarget: 0.18,
      timeToMarketTarget: 0.35
    }
  },

  {
    _id: "template_quick_cost_reduction",
    name: "Quick Start: Cost Reduction Focus",
    description: "Focus on operational efficiency and cost optimization",
    industry: "manufacturing",
    tier: "Cost Sensitive",
    persona: "Operations Director",
    turnover: 80000000,
    projectionYear: 3,
    totalImpact: 20000000,
    createdAt: "2024-01-11T00:00:00Z",
    isTemplate: true,
    templateCategory: "quickstart",
    
    values: {
      engineeringCost: { current: 6, target: 8 },
      errorCost: { current: 5, target: 7 },
      delayPenalty: { current: 5, target: 7 },
      systemAutomation: { current: 3, target: 6 },
      manualExceptions: { current: 5, target: 3 },
      reusePotential: { current: 4, target: 7 },
      reuseLibrary: { current: 3, target: 6 }
    },
    
    parameters: {
      annualGrowthRate: 3.5,
      discountRate: 7,
      implementationCostFactor: 0.15,
      riskAdjustmentFactor: 0.85,
      marginImprovementTarget: 0.15,
      efficiencyGainTarget: 0.22,
      qualityImprovementTarget: 0.20,
      timeToMarketTarget: 0.18
    }
  }
];

// ==================== HELPER FUNCTIONS ====================
export const getTemplateById = (id) => {
  return masterTemplateScenarios.find(template => template._id === id);
};

export const getTemplatesByCategory = (category) => {
  return masterTemplateScenarios.filter(template => template.templateCategory === category);
};

export const getAllTemplates = () => {
  return masterTemplateScenarios;
};

export const getTemplateCategories = () => {
  const categories = new Set();
  masterTemplateScenarios.forEach(template => {
    categories.add(template.templateCategory);
  });
  return Array.from(categories);
};

export const createUserScenarioFromTemplate = (templateId, userModifications = {}) => {
  const template = getTemplateById(templateId);
  if (!template) return null;
  
  const userScenario = {
    ...template,
    _id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name: `Copy of ${template.name}`,
    createdAt: new Date().toISOString(),
    isTemplate: false,
    userId: null,
    ...userModifications,
    values: {
      ...template.values,
      ...(userModifications.values || {})
    },
    parameters: {
      ...template.parameters,
      ...(userModifications.parameters || {})
    }
  };
  
  return userScenario;
};

// ==================== TEMPLATE LOADER COMPONENT ====================
export const MasterTemplateDialog = React.memo(({ open, onClose, onTemplateSelect }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Get categories from master templates
  const categories = useMemo(() => {
    const cats = new Set();
    masterTemplateScenarios.forEach(template => {
      cats.add(template.templateCategory);
    });
    return ['all', ...Array.from(cats)];
  }, []);

  // Filter templates by category
  const filteredTemplates = useMemo(() => {
    if (selectedCategory === 'all') return masterTemplateScenarios;
    return masterTemplateScenarios.filter(t => t.templateCategory === selectedCategory);
  }, [selectedCategory]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format category name for display
  const formatCategoryName = (category) => {
    if (category === 'all') return 'All Templates';
    if (category === 'quickstart') return 'Quick Start';
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const handleLoadTemplate = () => {
    if (!selectedTemplate) return;
    
    // Create user scenario from template
    const userScenario = {
      ...selectedTemplate,
      _id: `template_${Date.now()}`,
      name: `Copy of ${selectedTemplate.name}`,
      createdAt: new Date().toISOString(),
      isTemplate: true
    };
    
    onTemplateSelect(userScenario);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RocketLaunch />
          <Typography variant="h6" fontWeight="bold">
            Master Template Library
          </Typography>
        </Box>
        <Chip 
          label={`${masterTemplateScenarios.length} Templates`} 
          size="small"
          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
        />
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {/* Category Filter */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Select Industry Category:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {categories.map(category => (
              <Chip
                key={category}
                label={formatCategoryName(category)}
                onClick={() => setSelectedCategory(category)}
                color={selectedCategory === category ? "primary" : "default"}
                variant={selectedCategory === category ? "filled" : "outlined"}
                size="medium"
                sx={{ mb: 1 }}
              />
            ))}
          </Box>
        </Box>

        {/* Search and Info */}
        <Box sx={{ 
          mb: 3, 
          p: 2, 
          bgcolor: 'primary.50', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'primary.100'
        }}>
          <Typography variant="body2" color="primary.800">
            💡 <strong>Expert Templates:</strong> These pre-configured scenarios are based on 100+ years of industry experience. 
            Select a template matching your industry to get started instantly.
          </Typography>
        </Box>

        {/* Templates Grid */}
        <Grid container spacing={3}>
          {filteredTemplates.map(template => {
            const isSelected = selectedTemplate?._id === template._id;
            const improvementCount = Object.values(template.values).filter(v => v.target > v.current).length;
            const totalImprovement = Object.values(template.values).reduce((sum, v) => sum + (v.target - v.current), 0);
            
            return (
              <Grid item xs={12} md={6} lg={4} key={template._id}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    border: isSelected ? `3px solid ${theme.palette.primary.main}` : '1px solid',
                    borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
                    bgcolor: isSelected ? 'primary.50' : 'background.paper',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[6]
                    }
                  }}
                  onClick={() => handleTemplateSelect(template)}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    {/* Template Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          {template.name}
                        </Typography>
                        <Chip 
                          label={template.templateCategory === 'quickstart' ? 'Quick Start' : template.industry}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 1 }}
                        />
                      </Box>
                      {isSelected && (
                        <CheckCircle color="primary" sx={{ fontSize: 24 }} />
                      )}
                    </Box>

                    {/* Description */}
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
                      {template.description}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {/* Key Metrics */}
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Potential Impact
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="primary">
                          {formatCurrency(template.totalImpact)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Industry
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {template.industry}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Target Tier
                        </Typography>
                        <Typography variant="body2">
                          {template.tier}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Improvements
                        </Typography>
                        <Typography variant="body2" color="success.main" fontWeight="medium">
                          {improvementCount} factors (+{totalImprovement} total)
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Quick Preview of Key Changes */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Key Focus Areas:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {/* Show top 3 improving factors */}
                        {Object.entries(template.values)
                          .filter(([_, v]) => v.target > v.current)
                          .sort((a, b) => (b[1].target - b[1].current) - (a[1].target - a[1].current))
                          .slice(0, 3)
                          .map(([key, value]) => (
                            <Chip
                              key={key}
                              label={`${key.substring(0, 15)}... +${value.target - value.current}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleLoadTemplate}
          disabled={!selectedTemplate}
          variant="contained"
          startIcon={<RocketLaunch />}
          sx={{ borderRadius: 2 }}
        >
          Load Selected Template
        </Button>
      </DialogActions>
    </Dialog>
  );
});

MasterTemplateDialog.displayName = 'MasterTemplateDialog';

// Default export
export default {
  masterTemplateScenarios,
  getTemplateById,
  getTemplatesByCategory,
  getAllTemplates,
  getTemplateCategories,
  createUserScenarioFromTemplate,
  MasterTemplateDialog
};